import { useTable, useSortBy } from 'react-table';
import { useMemo, useReducer, useEffect } from 'react';
import { FaArrowDown, FaArrowUp, FaTrashAlt } from 'react-icons/fa';
import { useQuery } from '@apollo/client';
import { useNavigate } from 'react-router-dom';

import { useAuth } from '../../hooks/useAuth';
import {
  canUploadFiles,
  canOrderAudience,
  canOrderEnhancement,
  canOrderProfile,
  canUseModelFiles,
  isViewOnly,
} from '../../utils';

import Button from '../../components/inputs/Button';
import ActionModal from '../../components/ActionModal';
import ToastNotification from '../../components/ToastNotification';
import { FileImportSelector } from '../../components/actions/FileImportSelector';
import ActionsDropdown from '../../components/ActionsDropdown';
import LoadingSpinner from '../../components/LoadingSpinner';
import ErrorText from '../../components/ErrorText';
import FileStatusBadge from '../../components/FileStatusBadge';
import {
  formatDateCellShort,
  formatNumberWithCommas,
} from '../../helpers/reactTable';
import { FILES_QUERY } from '../../queries';
import {
  CLEAR_SUCCESS,
  initialState,
  reducer,
  START_ACTION,
} from '../../reducers/actionReducer';
import DeleteFile from '../../components/actions/DeleteFile';
import FileErrorDetails from '../../components/actions/FileErrorDetails';
import { isIdFile } from '../../helpers/fileKinds';
import { Tooltip } from 'react-tooltip';

// File statuses that indicate a failure and should surface a "View error"
// link in the Details cell. Mirrors the *-failed enum values in
// server/db/models/file.model.js plus the generic 'failed'.
const FAILED_STATUSES = [
  'uploading-failed',
  'processing-failed',
  'matching-failed',
  'failed',
];

// ATAD-136 / ATAD-146: the Order Type cell. Anything else is an ID file.
const FILE_KIND_LABELS: Record<string, string> = {
  zip: 'ZIP codes',
  model: 'Model',
};

// ATAD-146: ID files imported from an Alliant data source, by File.dataSource.
// (CMDA imports are model files, labelled by kind above.)
const DATA_SOURCE_LABELS: Record<string, string> = {
  'first-party': 'First Party',
  'syndicated-alliant': 'Syndicated Alliant',
};

// ATAD-136 / ATAD-146: delete-dialog titles. Anything else is an ID file.
const DELETE_TITLES: Record<string, string> = {
  zip: 'Delete ZIP Code File',
  model: 'Delete Model File',
};

function UploadedFilesTable({ data, dispatchAction, user }) {
  const navigate = useNavigate();

  const columns = useMemo(
    () => [
      {
        Header: 'Name',
        accessor: 'name',
      },
      {
        Header: 'Uploaded Date',
        accessor: 'createdAt',
        Cell: formatDateCellShort,
      },
      {
        Header: 'Order Type',
        id: 'orderType',
        // ATAD-136: ZIP code files are filters, not matched ID files.
        // ATAD-146: and so are model files.
        Cell: ({ row }) =>
          FILE_KIND_LABELS[row.original.kind] ??
          DATA_SOURCE_LABELS[row.original.dataSource] ??
          'File',
      },
      {
        Header: 'Actions',
        id: 'actions',
        Cell: ({ row }) => {
          const { status } = row.original;
          // ATAD-136: ZIP code files can only be deleted here — they are
          // used from the Audience Builder's ZIP Code Files list, and there
          // are no matched records to enhance, profile or filter with.
          // ATAD-146: the same goes for model files (Model Files list).
          const canPerformActions = status === 'completed' && isIdFile(row.original);
          
          const handleFilterWith = () => {
            navigate('../../audiences/audiencebuilder', {
              state: {
                initialLoadState: {
                  initialFileInclude: {
                    id: row.original.id,
                    name: row.original.name,
                    type: 'file',
                  },
                },
              },
            });
          };
          
          const handleEnhance = () => {
            navigate(`../../enhancements/${row.original.id}`);
          };
          
          const handleProfile = () => {
            navigate(`../../insights/${row.original.id}`);
          };
          
          const handleDelete = () => {
            dispatchAction({
              type: START_ACTION,
              payload: {
                name: DELETE_TITLES[row.original.kind] ?? 'Delete File',
                component: (
                  <DeleteFile
                    dispatchAction={dispatchAction}
                    fileName={row.original.name}
                    fileId={row.original.id}
                  />
                ),
              },
            });
          };
          
          // Create a custom dropdown for files with specific file actions
          return (
            <div className="relative">
              <ActionsDropdown
                row={row}
                orderType="file"
                showDownload={false}
                showActivate={false}
                showEnhance={canOrderEnhancement(user) && canPerformActions}
                showProfile={canOrderProfile(user) && canPerformActions && !row.original.hasBeenProfiled}
                showEdit={canOrderAudience(user) && canPerformActions}
                showDelete={true}
                showDuplicate={false}
                editLabel="Filter with"
                onEnhance={handleEnhance}
                onProfile={handleProfile}
                onEdit={handleFilterWith}
                onDelete={handleDelete}
              />
            </div>
          );
        },
      },
      {
        Header: 'Price',
        id: 'price',
        Cell: () => '--',
      },
      {
        Header: 'Receipt',
        id: 'receipt',
        Cell: () => '--',
      },
      {
        Header: 'Details',
        id: 'details',
        Cell: ({ row }) => {
          const isFailed = FAILED_STATUSES.includes(row.original.status);

          const handleViewError = () => {
            dispatchAction({
              type: START_ACTION,
              payload: {
                name: 'File processing error',
                noContinue: true,
                component: (
                  <FileErrorDetails
                    fileId={row.original.id}
                    fileName={row.original.name}
                    status={row.original.status}
                    errorStage={row.original.errorStage}
                    errorMessage={row.original.errorMessage}
                  />
                ),
              },
            });
          };

          const formatCount = (n: number | null | undefined) =>
            n != null ? formatNumberWithCommas({ value: n }) : '—';

          const formatRate = (r: number | null | undefined) =>
            r != null ? `${(r * 100).toFixed(2)}%` : '—';

          // ATAD-136: rows read / ZIPs kept / rows skipped instead of match stats.
          if (row.original.kind === 'zip') {
            return (
              <div className="flex flex-col gap-2 min-w-[12rem]">
                <FileStatusBadge status={row.original.status} />
                <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-0.5 text-sm text-gray-700">
                  <dt className="text-gray-500">Rows</dt>
                  <dd className="text-right tabular-nums text-gray-900">
                    {formatCount(row.original.records)}
                  </dd>
                  <dt className="text-gray-500">ZIPs loaded</dt>
                  <dd className="text-right tabular-nums text-gray-900">
                    {formatCount(row.original.numUniqueRecords)}
                  </dd>
                  <dt className="text-gray-500">Rows skipped</dt>
                  <dd className="text-right tabular-nums text-gray-900">
                    {formatCount(row.original.numSkippedRecords)}
                  </dd>
                </dl>
              </div>
            );
          }

          // ATAD-146: rows read / people imported / rows skipped. A failed
          // import's message is readable (bad scores, too many rows skipped),
          // so it gets "View error" like an ID file.
          if (row.original.kind === 'model') {
            return (
              <div className="flex flex-col gap-2 min-w-[12rem]">
                <FileStatusBadge
                  status={row.original.status}
                  kind="model"
                  onViewError={isFailed ? handleViewError : undefined}
                />
                <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-0.5 text-sm text-gray-700">
                  <dt className="text-gray-500">Rows</dt>
                  <dd className="text-right tabular-nums text-gray-900">
                    {formatCount(row.original.records)}
                  </dd>
                  <dt className="text-gray-500">People imported</dt>
                  <dd className="text-right tabular-nums text-gray-900">
                    {formatCount(row.original.numUniqueRecords)}
                  </dd>
                  <dt className="text-gray-500">Rows skipped</dt>
                  <dd className="text-right tabular-nums text-gray-900">
                    {formatCount(row.original.numSkippedRecords)}
                  </dd>
                </dl>
              </div>
            );
          }

          return (
            <div className="flex flex-col gap-2 min-w-[12rem]">
              <FileStatusBadge
                status={row.original.status}
                imported={!!row.original.dataSource}
                onViewError={isFailed ? handleViewError : undefined}
              />
              <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-0.5 text-sm text-gray-700">
                <dt className="text-gray-500">Uploaded</dt>
                <dd className="text-right tabular-nums text-gray-900">
                  {formatCount(row.original.records)}
                </dd>
                <dt className="text-gray-500">Unique</dt>
                <dd className="text-right tabular-nums text-gray-900">
                  {formatCount(row.original.numUniqueRecords)}
                </dd>
                <dt className="text-gray-500">Matched</dt>
                <dd className="text-right tabular-nums text-gray-900">
                  {formatCount(row.original.numMatchedRecords)}
                </dd>
                <dt className="text-gray-500">Match rate</dt>
                <dd className="text-right tabular-nums text-gray-900">
                  {formatRate(row.original.matchRate)}
                </dd>
              </dl>
            </div>
          );
        },
      },
    ],
    [user]
  );

  const initialSortBy = useMemo(() => [{ id: 'uploadDate', desc: true }], []);

  const tableInstance = useTable(
    {
      columns,
      data,
      initialState: {
        sortBy: initialSortBy,
      },
    },
    useSortBy
  );

  const { getTableProps, getTableBodyProps, headerGroups, rows, prepareRow } =
    tableInstance;

  return (
    <div className="h-full overflow-auto" style={{ paddingBottom: rows.length > 0 ? '120px' : '24px' }}>
      <div className="overflow-visible">
        <table {...getTableProps()} className="w-full">
        <thead className="whitespace-nowrap">
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  className="border-b-2 border-black p-2 hover:bg-gray-100"
                >
                  <div className="flex items-center">
                    {column.render('Header')}
                    {column.isSorted && (
                      <span className="pl-2">
                        {column.isSortedDesc ? <FaArrowDown /> : <FaArrowUp />}
                      </span>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()} className="whitespace-nowrap">
          {rows.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell) => (
                  <td className="p-2" {...cell.getCellProps()}>
                    {cell.render('Cell')}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
      {rows.length === 0 && (
        <p className="text-center pt-6">You haven't uploaded any files yet.</p>
      )}
      </div>
    </div>
  );
}

export default function UploadedFiles() {
  const { user } = useAuth();
  const { loading, error, data, startPolling, stopPolling } = useQuery(
    FILES_QUERY,
    {
      fetchPolicy: 'cache-and-network',
    }
  );
  const files = data?.files;
  const allFilesIdle = files?.every((file) =>
    ['completed', 'processing-failed', 'matching-failed'].includes(file.status)
  );

  useEffect(
    function managePollForStatusUpdates() {
      if (files && files.length > 0) {
        if (!allFilesIdle) {
          startPolling(1000);
        } else {
          stopPolling();
        }
      } else {
        stopPolling();
      }
    },
    [files]
  );

  const [actionState, dispatchAction] = useReducer(reducer, initialState);
  const { success, nextAction } = actionState;

  useEffect(() => {
    if (success) {
      setTimeout(
        () =>
          dispatchAction({
            type: CLEAR_SUCCESS,
          }),
        5000
      );
    }
  }, [success, nextAction]);

  return (
    <>
      <div className="w-full flex flex-col">
        {loading && (
          <LoadingSpinner message="Getting your files..." className="mt-10" />
        )}
        {!loading && error && (
          <ErrorText message={error.message} className="ml-0" />
        )}
        {!loading && !error && data && files && (
          <>
            {canUploadFiles(user) && (
              <div className="flex flex-row justify-end pb-4">
                <Button
                  type="button"
                  primary
                  onClick={() =>
                    dispatchAction({
                      type: START_ACTION,
                      payload: {
                        name: 'Upload File',
                        component: (
                          <FileImportSelector
                            dispatchAction={dispatchAction}
                            allowZipFiles
                            allowDataSources={canUseModelFiles(user)}
                          />
                        ),
                        // ATAD-146: the two-pane sources modal.
                        layout: 'panes',
                        showCancel: true,
                      },
                    })
                  }
                >
                  Add a New File
                </Button>
              </div>
            )}

            {files.length === 0 && !canUploadFiles(user) && isViewOnly(user) && (
              <div className="text-center py-8 text-gray-600">
                <p>No files have been uploaded yet. Contact your administrator for file access.</p>
              </div>
            )}

            {files.length > 0 && (
              <UploadedFilesTable data={files} dispatchAction={dispatchAction} user={user} />
            )}
          </>
        )}
        {actionState.inProgress && (
          <ActionModal
            actionState={actionState}
            dispatchAction={dispatchAction}
            show={actionState.inProgress}
          />
        )}
        <ToastNotification
          message={actionState.success}
          success={true}
          show={actionState.success}
        />
      </div>
      <Tooltip
        id="has-been-profiled-tooltip"
        place="top"
        style={{ maxWidth: 300 }}
      />
    </>
  );
}
